import {
  forwardRef,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

import { cn } from '../../lib/utils';

export type PopoverPosition = 'top' | 'bottom' | 'left' | 'right';

const POPOVER_GAP = 6;
/** Marge minimale entre le panel et les bords de l'ecran. */
const POPOVER_VIEWPORT_MARGIN = 8;

interface PopoverCoords {
  top: number;
  left: number;
  /** Pose seulement quand le panel est plus haut que l'ecran. */
  maxHeight?: number;
}

/**
 * Coin haut-gauche du panel (coordonnees `fixed`, donc relatives a l'ecran).
 * Meme placement qu'avant (centre sur le declencheur, cote `position`) ;
 * ensuite, seulement si le panel sortirait de l'ecran : bascule du cote
 * oppose s'il y tient (haut <-> bas, gauche <-> droite), puis decalage pour
 * rester a 8 px des bords ; s'il est plus haut que l'ecran, il defile.
 */
export function computePopoverPosition(
  trigger: Pick<DOMRect, 'top' | 'bottom' | 'left' | 'right' | 'width' | 'height'>,
  panel: { width: number; height: number },
  position: PopoverPosition,
  viewport: { width: number; height: number }
): PopoverCoords {
  const m = POPOVER_VIEWPORT_MARGIN;
  const { width: w, height: h } = panel;
  const centerX = trigger.left + trigger.width / 2 - w / 2;
  const centerY = trigger.top + trigger.height / 2 - h / 2;
  const below = trigger.bottom + POPOVER_GAP;
  const above = trigger.top - POPOVER_GAP - h;
  const toRight = trigger.right + POPOVER_GAP;
  const toLeft = trigger.left - POPOVER_GAP - w;

  let top: number;
  let left: number;
  switch (position) {
    case 'top':
      top = above < m && below + h <= viewport.height - m ? below : above;
      left = centerX;
      break;
    case 'bottom':
      top = below + h > viewport.height - m && above >= m ? above : below;
      left = centerX;
      break;
    case 'left':
      left = toLeft < m && toRight + w <= viewport.width - m ? toRight : toLeft;
      top = centerY;
      break;
    case 'right':
      left = toRight + w > viewport.width - m && toLeft >= m ? toLeft : toRight;
      top = centerY;
      break;
  }

  left = Math.max(m, Math.min(left, viewport.width - w - m));
  const availableHeight = viewport.height - 2 * m;
  if (h > availableHeight) return { top: m, left, maxHeight: availableHeight };
  top = Math.max(m, Math.min(top, viewport.height - h - m));
  return { top, left };
}

export interface PopoverProps {
  /** Element declencheur (cliquable). Recoit un wrapper qui gere le toggle. */
  trigger: ReactNode;
  /** Contenu du popover (rendu au clic). */
  children: ReactNode;
  /** Position relative au trigger. Default `bottom`. */
  position?: PopoverPosition;
  /** Largeur max du panel. Default `260px`. */
  maxWidth?: number | string;
  /** Classe additionnelle sur le panel. */
  className?: string;
  /** Classe additionnelle sur le wrapper du trigger. */
  triggerClassName?: string;
  /** Stop propagation du clic du trigger (utile dans des cards/rows cliquables). */
  stopPropagation?: boolean;
  /** Callback ouverture / fermeture. */
  onOpenChange?: (open: boolean) => void;
}

/**
 * Popover click-to-open avec backdrop + positionnement viewport-aware via
 * portal. Utilise pour des contenus interactifs (overflow tags "+N", filtres
 * inline, mini-menus). Pour un info au survol, prefere `Tooltip`.
 *
 * Le panel est rendu dans un portail au niveau `document.body` pour eviter
 * les soucis d'overflow / z-index dans les containers parents.
 */
export const Popover = forwardRef<HTMLDivElement, PopoverProps>(
  (
    {
      trigger,
      children,
      position = 'bottom',
      maxWidth = 260,
      className,
      triggerClassName,
      stopPropagation = true,
      onOpenChange,
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const triggerRef = useRef<HTMLDivElement | null>(null);
    const panelRef = useRef<HTMLDivElement | null>(null);
    const [coords, setCoords] = useState<PopoverCoords>({ top: 0, left: 0 });

    const setOpen = (next: boolean) => {
      setIsOpen(next);
      onOpenChange?.(next);
    };

    // Avant l'affichage (layout effect) : le panel est monte, on connait sa
    // taille, on le place puis on le garde dans l'ecran.
    useLayoutEffect(() => {
      if (!isOpen || !triggerRef.current || !panelRef.current) return;
      const panel = panelRef.current;
      setCoords(
        computePopoverPosition(
          triggerRef.current.getBoundingClientRect(),
          { width: panel.offsetWidth, height: panel.scrollHeight + 2 },
          position,
          { width: window.innerWidth, height: window.innerHeight }
        )
      );
    }, [isOpen, position]);

    // Fermeture au scroll / resize pour eviter un panel orphelin.
    useEffect(() => {
      if (!isOpen) return;
      const close = () => setOpen(false);
      window.addEventListener('scroll', close, true);
      window.addEventListener('resize', close);
      return () => {
        window.removeEventListener('scroll', close, true);
        window.removeEventListener('resize', close);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const cssMaxWidth = typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth;

    return (
      <>
        <div
          ref={(node) => {
            triggerRef.current = node;
            if (typeof ref === 'function') ref(node);
            else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
          }}
          role="button"
          tabIndex={0}
          onClick={(e) => {
            if (stopPropagation) e.stopPropagation();
            setOpen(!isOpen);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              if (stopPropagation) e.stopPropagation();
              setOpen(!isOpen);
            } else if (e.key === 'Escape' && isOpen) {
              setOpen(false);
            }
          }}
          className={cn('inline-flex cursor-pointer', triggerClassName)}
        >
          {trigger}
        </div>

        {isOpen &&
          createPortal(
            <>
              <div
                className="fixed inset-0 z-[9998]"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                }}
              />
              <div
                ref={panelRef}
                role="dialog"
                onClick={(e) => e.stopPropagation()}
                className={cn(
                  // `w-max` : largeur du contenu quelle que soit la position
                  // (sinon un panel pres du bord droit se tasserait).
                  'fixed z-[9999] w-max rounded-lg border border-neutral-200 bg-white p-2 shadow-lg',
                  'animate-in fade-in-0 zoom-in-95',
                  className
                )}
                style={{
                  top: coords.top,
                  left: coords.left,
                  maxWidth: `min(${cssMaxWidth}, calc(100vw - ${2 * POPOVER_VIEWPORT_MARGIN}px))`,
                  ...(coords.maxHeight !== undefined && {
                    maxHeight: coords.maxHeight,
                    overflowY: 'auto' as const,
                  }),
                }}
              >
                {children}
              </div>
            </>,
            document.body
          )}
      </>
    );
  }
);

Popover.displayName = 'Popover';
